/**
 * 🚀 UNIFIED IMAGE PROCESSING PIPELINE
 * Handles compression, validation, moderation, and storage upload
 * Apple App Store compliant with strict error handling
 */

import { moderateContent } from './contentModeration';

// Ultra-aggressive compression settings for mobile optimization
const COMPRESSION_CONFIG = {
  maxWidth: 800,
  maxHeight: 600,
  quality: 0.7,
  format: 'webp'
};

// Hash function for de-duplication
async function quickHash(blob) {
  const buffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Session cache for moderation results
const moderationCache = new Map();

export async function prepareImageBlob(input) {
  let blob;

  console.log("🔄 Starting image blob preparation...");

  // Step 1: Resolve to a Blob
  if (typeof input === 'string') {
    // URL input - handle CORS issues gracefully
    try {
      console.log("📥 Attempting to fetch image from URL:", input.substring(0, 100) + "...");
      
      // Shorter timeout for mobile optimization
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      let response;
      try {
        // Try with CORS first (for proper content validation)
        response = await fetch(input, {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors',
          headers: {
            'Accept': 'image/*,*/*;q=0.8',
            'Cache-Control': 'no-cache'
          }
        });
      } catch (corsError) {
        console.log("⚠️ CORS fetch failed, trying no-cors mode:", corsError.message);
        
        // Fallback to no-cors mode
        try {
          response = await fetch(input, {
            method: 'GET',
            signal: controller.signal,
            mode: 'no-cors'
          });
        } catch (noCorsError) {
          console.log("⚠️ No-cors also failed:", noCorsError.message);
          throw corsError; // Throw original CORS error
        }
      }
      
      clearTimeout(timeoutId);
      
      if (!response) {
        throw new Error('NETWORK_ERROR');
      }
      
      // Handle response based on type
      if (response.type === 'opaque') {
        console.log("🔄 No-cors response received, creating blob...");
        blob = await response.blob();
      } else {
        if (!response.ok) {
          console.error(`❌ HTTP ${response.status}: ${response.statusText}`);
          throw new Error(`HTTP_${response.status}`);
        }
        
        // Check content type when available
        const contentType = response.headers.get('content-type');
        if (contentType && !contentType.startsWith('image/')) {
          console.warn("⚠️ Content-Type is not image:", contentType);
          // Continue anyway, some servers don't set proper content-type
        }
        
        blob = await response.blob();
      }
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('EMPTY_RESPONSE');
      }
      
      console.log("✅ Image fetched successfully, size:", Math.round(blob.size / 1024), "KB");
      
    } catch (error) {
      console.error("❌ Failed to fetch image from URL:", error);
      if (error.name === 'AbortError') {
        throw new Error('TIMEOUT');
      } else if (error.message === 'EMPTY_RESPONSE') {
        throw new Error('INVALID_IMAGE_URL');
      } else if (error.message.startsWith('HTTP_')) {
        throw new Error('NETWORK_ERROR');
      } else if (error.message.includes('CORS') || error.message.includes('cors')) {
        throw new Error('CORS_ERROR');
      } else {
        throw new Error('NETWORK_ERROR');
      }
    }
  } else if (input instanceof File || input instanceof Blob) {
    blob = input;
    console.log("✅ Using provided file/blob, size:", Math.round(blob.size / 1024), "KB");
  } else {
    console.error("❌ Invalid input type provided to prepareImageBlob");
    throw new Error('invalid_input');
  }

  // Step 2: Normalize MIME - be more flexible for mobile
  const validMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  if (!validMimes.includes(blob.type.toLowerCase())) {
    // For mobile compatibility, try to process anyway if it's an image
    if (!blob.type.startsWith('image/')) {
      throw new Error('invalid_mime');
    }
  }

  // Step 3 & 4: Decode and resize - more aggressive for mobile
  let bitmap;
  try {
    console.log("🎨 Creating image bitmap...");
    // Try modern createImageBitmap first (faster)
    bitmap = await createImageBitmap(blob);
    console.log("✅ Bitmap created successfully:", bitmap.width + "x" + bitmap.height);
  } catch (error) {
    console.log("⚠️ createImageBitmap failed, falling back to canvas approach:", error.message);
    // Fallback to traditional canvas approach
    try {
      return await fallbackImageProcess(blob);
    } catch (fallbackError) {
      console.error("❌ Both bitmap creation and fallback failed:", fallbackError);
      throw new Error('image_decode_failed');
    }
  }

  const { width: originalWidth, height: originalHeight } = bitmap;

  // Ultra-fast sizing for mobile optimization
  const maxDim = 400; // Even smaller for ultra-fast processing
  const optimalDim = 250; // Ultra-small for speed

  let newWidth = originalWidth;
  let newHeight = originalHeight;

  // Always resize for mobile optimization - prioritize speed over quality
  const ratio = Math.min(maxDim / originalWidth, maxDim / originalHeight);
  if (ratio < 1) {
    newWidth = Math.round(originalWidth * ratio);
    newHeight = Math.round(originalHeight * ratio);
  } else if (originalWidth > optimalDim || originalHeight > optimalDim) {
    const optimalRatio = Math.min(optimalDim / originalWidth, optimalDim / originalHeight);
    newWidth = Math.round(originalWidth * optimalRatio);
    newHeight = Math.round(originalHeight * optimalRatio);
  }

  // Step 5: Convert and compress
  let canvas;
  let ctx;

  try {
    // Try OffscreenCanvas for better performance
    canvas = new OffscreenCanvas(newWidth, newHeight);
    ctx = canvas.getContext('2d');
  } catch (error) {
    // Fallback to regular canvas
    canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    ctx = canvas.getContext('2d');
  }

  // Draw resized image
  ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);

  // Step 6 & 7: Convert to optimized format with ultra-aggressive compression
  let quality = 0.75; // Start with lower quality
  let outputBlob;
  let outputMime;

  // Use JPEG for better mobile compatibility and smallest sizes
  const createBlob = async (q) => {
    if (canvas.convertToBlob) {
      return await canvas.convertToBlob({ type: 'image/jpeg', quality: q });
    } else {
      return await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', q)
      );
    }
  };

  // Ultra-fast compression - single attempt
  const maxSizeBytes = 150 * 1024; // 150KB max
  let attempts = 0;
  const maxAttempts = 2; // Maximum 2 attempts for speed

  do {
    outputBlob = await createBlob(quality);

    if (outputBlob.size <= maxSizeBytes || attempts >= maxAttempts) {
      break;
    }

    // Single quality reduction step
    quality = 0.4; // Jump to low quality immediately
    attempts++;

    console.log(`🗜️ Ultra-fast compression attempt ${attempts}: ${Math.round(outputBlob.size / 1024)}KB with quality ${quality.toFixed(2)}`);

  } while (attempts < maxAttempts);

  outputMime = 'image/jpeg';

  console.log(`✅ Final mobile image: ${Math.round(outputBlob.size / 1024)}KB, ${newWidth}x${newHeight}, quality: ${quality.toFixed(2)}`);

  // If still too big after 2 attempts, accept it - prevent timeout loops
  if (outputBlob.size > maxSizeBytes) {
    console.log(`⚠️ Image larger than target (${Math.round(outputBlob.size / 1024)}KB) but proceeding to prevent timeout`);
  }

  bitmap.close();

  return {
    blob: outputBlob,
    mime: outputMime,
    width: newWidth,
    height: newHeight
  };
}

async function fallbackImageProcess(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const { width: originalWidth, height: originalHeight } = img;
      const maxDim = 800; // Smaller for mobile

      let newWidth = originalWidth;
      let newHeight = originalHeight;

      // Always resize for mobile
      if (originalWidth > maxDim || originalHeight > maxDim) {
        const ratio = Math.min(maxDim / originalWidth, maxDim / originalHeight);
        newWidth = Math.round(originalWidth * ratio);
        newHeight = Math.round(originalHeight * ratio);
      }

      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Ultra-aggressive progressive compression for fallback
      let quality = 0.7;
      const maxSize = 150 * 1024; // Match main pipeline - 150KB max
      let attempts = 0;

      const tryCompress = (q) => {
        canvas.toBlob((outputBlob) => {
          if (!outputBlob) {
            reject(new Error('compression_failed'));
            return;
          }

          if (outputBlob.size <= maxSize || attempts >= 8 || q <= 0.2) {
            console.log(`🗜️ Fallback ultra-compression complete: ${Math.round(outputBlob.size / 1024)}KB`);
            resolve({
              blob: outputBlob,
              mime: 'image/jpeg',
              width: newWidth,
              height: newHeight
            });
          } else {
            attempts++;
            quality -= 0.1; // More gradual reduction for better optimization
            console.log(`🗜️ Fallback compression attempt ${attempts}: trying quality ${quality.toFixed(2)}`);
            tryCompress(quality);
          }
        }, 'image/jpeg', q);
      };

      tryCompress(quality);
    };

    img.onerror = () => reject(new Error('image_decode_failed'));
    img.src = URL.createObjectURL(blob);
  });
}

export async function moderateImageBlob(blob, isProductImage = true) {
  // Use unified moderation system
  const { moderateContent } = await import('./contentModeration.js');

  const context = isProductImage ? 'product' : 'user_content';
  const result = await moderateContent(blob, context);

  if (!result.allowed) {
    console.log(`🚫 Image blocked: ${result.reason}`);
  }

  return result.allowed;
}

// uploadImageBlob function removed - now using base64 storage in Firestore directly

/**
 * Validate file before processing
 */
function validateFile(file) {
  // Size check (25MB limit)
  const MAX_FILE_SIZE = 25 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('FILE_TOO_LARGE');
  }

  // Type check
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('INVALID_FILE_TYPE');
  }

  return true;
}

/**
 * Check if adding an image would exceed storage limits
 */
export function checkStorageLimit(products = []) {
  if (isWishlistAtLimit(products)) {
    throw new Error('STORAGE_LIMIT_EXCEEDED');
  }
  return true;
}